import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useGame, BUILDING_COIN_INCOME, BlockData, BuildingType } from "@/contexts/GameContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useLang } from "@/contexts/LanguageContext";

const BUILDING_ICONS: Record<string, string> = {
  house: "ri-home-4-line",
  company: "ri-building-4-line",
  powerPlant: "ri-flashlight-line",
};

const BUILDING_COLORS: Record<string, string> = {
  house: "text-emerald-400 bg-emerald-400/10",
  company: "text-sky-400 bg-sky-400/10",
  powerPlant: "text-yellow-400 bg-yellow-400/10",
};

type SortKey = "lat" | "buildings" | "income" | "status";

function calcIncome(block: BlockData): number {
  return block.buildings.reduce((s, b) => s + BUILDING_COIN_INCOME[b.type], 0);
}

export default function OwnedBlocksList() {
  const { user } = useAuth();
  const { blocks, companies, powerPlants } = useGame();
  const { isDark } = useTheme();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>("lat");
  const [filterStatus, setFilterStatus] = useState<"all" | "owned" | "auction">("all");

  const BUILDING_LABELS: Record<string, string> = {
    house: t("buildingHouseLabel"),
    company: t("buildingCompanyLabel"),
    powerPlant: t("buildingPowerPlantLabel"),
  };

  const myBlocks = user ? Object.values(blocks).filter((b) => b.ownerId === user.id) : [];

  const filtered = myBlocks.filter((b) => {
    if (filterStatus === "owned") return !b.isForSale;
    if (filterStatus === "auction") return b.isForSale;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === "buildings") return b.buildings.length - a.buildings.length;
    if (sortKey === "income") return calcIncome(b) - calcIncome(a);
    if (sortKey === "status") return Number(b.isForSale) - Number(a.isForSale);
    return a.lat - b.lat;
  });

  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const hoverBg = isDark ? "hover:bg-white/3" : "hover:bg-gray-50";

  const getBuildingName = (type: BuildingType, buildingId: string): string => {
    if (type === "company" && companies[buildingId]) return companies[buildingId].name;
    if (type === "powerPlant" && powerPlants[buildingId]) return powerPlants[buildingId].name;
    return BUILDING_LABELS[type];
  };

  const handleBuildingClick = (type: BuildingType, buildingId: string) => {
    if (type === "company") navigate(`/company/${buildingId}`);
    else if (type === "powerPlant") navigate(`/powerplant/${buildingId}`);
    else navigate(`/house/${buildingId}`);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
        <div>
          <h2 className={`text-lg font-bold ${textPrimary}`}>{t("ownedBlocksTitle")}</h2>
          <p className={`text-sm ${textSecondary}`}>{sorted.length} {t("unitBlocks")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className={`flex rounded-xl border overflow-hidden text-xs ${isDark ? "border-white/10" : "border-gray-200"}`}>
            {(["all", "owned", "auction"] as const).map((f) => (
              <button key={f} onClick={() => setFilterStatus(f)}
                className={`px-3 py-2 font-medium transition-colors cursor-pointer whitespace-nowrap ${filterStatus === f ? "bg-amber-400 text-black" : isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}>
                {f === "all" ? t("filterAll") : f === "owned" ? t("filterOwned") : t("filterAuction")}
              </button>
            ))}
          </div>
          <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}
            className={`text-xs rounded-xl border px-3 py-2 cursor-pointer outline-none ${isDark ? "bg-[#0f1015] border-white/10 text-gray-300" : "bg-white border-gray-200 text-gray-700"}`}>
            <option value="lat">{t("sortByCoords")}</option>
            <option value="buildings">{t("sortByBuildings")}</option>
            <option value="income">{t("sortByIncome")}</option>
            <option value="status">{t("sortByStatus")}</option>
          </select>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className={`rounded-2xl border p-12 text-center ${cardBg}`}>
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-amber-400/10 rounded-2xl">
            <i className="ri-map-pin-2-line text-3xl text-amber-400"></i>
          </div>
          <p className={`font-semibold mb-1 ${textPrimary}`}>{t("noBlocksTitle")}</p>
          <p className={`text-sm ${textSecondary}`}>{t("noBlocksDesc")}</p>
          <button onClick={() => navigate("/map")}
            className="mt-4 bg-amber-400 hover:bg-amber-300 text-black text-sm font-bold rounded-full px-6 py-2 transition-all cursor-pointer whitespace-nowrap">
            {t("goToMap")}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((block) => {
            const income = calcIncome(block);
            return (
              <div key={block.blockId} className={`rounded-2xl border overflow-hidden ${cardBg}`}>
                <div className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                      <i className="ri-map-pin-2-line text-amber-400 text-sm"></i>
                    </div>
                    <div>
                      <p className={`text-sm font-bold font-mono ${textPrimary}`}>
                        {block.lat.toFixed(4)}, {block.lng.toFixed(4)}
                      </p>
                      <p className={`text-xs ${textSecondary}`}>
                        {t("buildingCount")} {block.buildings.length} • {t("incomeLabel")} +{income} coins/h
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {block.isForSale ? (
                      <span className="text-xs bg-orange-400/15 text-orange-400 border border-orange-400/30 rounded-full px-2.5 py-1 font-medium whitespace-nowrap">
                        {t("onAuction")}
                      </span>
                    ) : (
                      <span className={`text-xs rounded-full px-2.5 py-1 font-medium whitespace-nowrap ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-500"}`}>
                        {t("holding")}
                      </span>
                    )}
                    <button onClick={() => navigate("/map")}
                      className={`text-xs rounded-lg px-3 py-1.5 font-medium transition-colors cursor-pointer whitespace-nowrap ${isDark ? "bg-white/5 hover:bg-amber-400/20 text-gray-400 hover:text-amber-400" : "bg-gray-100 hover:bg-amber-50 text-gray-500 hover:text-amber-600"}`}>
                      <i className="ri-map-pin-line mr-1"></i>{t("mapBtn")}
                    </button>
                  </div>
                </div>

                {block.buildings.length > 0 ? (
                  <div className={`divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}>
                    {block.buildings.map((bld, i) => {
                      const colorClass = BUILDING_COLORS[bld.type];
                      const [textColor, bgColor] = colorClass.split(" ");
                      const name = getBuildingName(bld.type, bld.buildingId);
                      return (
                        <button key={i} onClick={() => handleBuildingClick(bld.type, bld.buildingId)}
                          className={`w-full flex items-center gap-3 px-5 py-3 transition-colors cursor-pointer text-left ${hoverBg}`}>
                          <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${bgColor}`}>
                            <i className={`${BUILDING_ICONS[bld.type]} text-sm ${textColor}`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${textPrimary}`}>{name}</p>
                            <p className={`text-xs ${textSecondary}`}>
                              {BUILDING_LABELS[bld.type]} • {t("builtOn")} {new Date(bld.builtAt).toLocaleDateString(lang === "ja" ? "ja-JP" : "en-US")}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={`text-xs font-semibold ${textColor}`}>+{BUILDING_COIN_INCOME[bld.type]}/h</span>
                            <div className={`w-6 h-6 flex items-center justify-center rounded-md ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                              <i className={`ri-arrow-right-s-line text-xs ${textSecondary}`}></i>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`px-5 py-3 flex items-center gap-2 ${textSecondary}`}>
                    <i className="ri-add-circle-line text-sm text-amber-400"></i>
                    <span className="text-xs">{t("noBuildingOnBlock")}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
